import { create } from 'zustand';
import type { Order, SimulatedEmail } from '../types';
import { useSettingsStore } from './settingsStore';
import { useProductStore } from './productStore';
import { formatPriceRaw } from '../utils/price';
import { sendRealEmail } from '../services/emailService';
import { db, isFirebaseActive, removeUndefined } from '../firebase';
import { ref, set as dbSet, get, child } from 'firebase/database';

const sanitizeLocalEmail = (email: string) => email.toLowerCase().replace(/\./g, '_');
interface OrderState {
  orders: Order[];
  emails: SimulatedEmail[];
  addOrder: (order: Order) => Promise<void>;
  updateOrderStatus: (id: string, status: Order['status'], trackingNumber?: string) => Promise<void>;
  updateOrder: (id: string, updates: Partial<Order>) => Promise<void>;
  focusedOrderId?: string | null;
  deleteOrder: (id: string) => Promise<void>;
  addEmail: (email: SimulatedEmail) => Promise<void>;
  clearAllOrders: () => Promise<void>;
}

const mockOrders: Order[] = [
  {
    id: 'SZ-8472',
    customerDetails: {
      name: 'Nimal Perera',
      email: 'nimal@gmail.com',
      phone: '0773456789',
      address: 'No. 45, Flower Road',
      city: 'Colombo 07'
    },
    items: [
      {
        id: 'prod-arduino-uno',
        product: {
          id: 'prod-arduino-uno',
          name: 'Arduino Uno R3 Dev Board (DIP)',
          categoryId: 'modules',
          subCategoryId: 'dev-boards',
          shortDescription: 'The classic Arduino Uno microcontroller board based on the ATmega328P.',
          longDescription: '',
          price: 1850,
          stock: 25,
          imageUrl: 'https://images.unsplash.com/photo-1608564697071-ddf911d81370?auto=format&fit=crop&w=500&h=500&q=80',
          specs: [],
          rating: 4.8,
          reviewsCount: 34
        },
        quantity: 2
      },
      {
        id: 'prod-hcsr04',
        product: {
          id: 'prod-hcsr04',
          name: 'HC-SR04 Ultrasonic Distance Sensor Module',
          categoryId: 'sensors',
          subCategoryId: 'motion-distance',
          shortDescription: 'Ultrasonic range finder providing non-contact distance measurements from 2cm to 400cm.',
          longDescription: '',
          price: 320,
          stock: 120,
          imageUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=500&h=500&q=80',
          specs: [],
          rating: 4.6,
          reviewsCount: 88
        },
        quantity: 3
      }
    ],
    subtotal: 4660,
    deliveryCharge: 350,
    total: 5010,
    paymentMethod: 'Bank Transfer',
    status: 'Processing',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString() // 1 day ago
  },
  {
    id: 'SZ-7934',
    customerDetails: {
      name: 'Sanduni Jayawardena',
      email: 'sanduni@yahoo.com',
      phone: '0714567890',
      address: '2nd Lane, Temple Road',
      city: 'Kandy'
    },
    items: [
      {
        id: 'prod-esp32-nodemcu',
        product: {
          id: 'prod-esp32-nodemcu',
          name: 'ESP32 NodeMCU WIFI + Bluetooth Dev Board',
          categoryId: 'modules',
          subCategoryId: 'dev-boards',
          shortDescription: 'Dual-core MCU with integrated Wi-Fi and Bluetooth, ideal for IoT projects.',
          longDescription: '',
          price: 1450,
          stock: 42,
          imageUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=500&h=500&q=80',
          specs: [],
          rating: 4.9,
          reviewsCount: 52
        },
        quantity: 1
      },
      {
        id: 'prod-dht22',
        product: {
          id: 'prod-dht22',
          name: 'DHT22 Digital Temperature & Humidity Sensor',
          categoryId: 'sensors',
          subCategoryId: 'temp-humidity',
          shortDescription: 'High precision capacitive humidity and temperature sensor with digital output.',
          longDescription: '',
          price: 850,
          stock: 30,
          imageUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=500&h=500&q=80',
          specs: [],
          rating: 4.7,
          reviewsCount: 19
        },
        quantity: 1
      }
    ],
    subtotal: 2300,
    deliveryCharge: 350,
    total: 2650,
    paymentMethod: 'Bank Transfer',
    status: 'Completed',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString() // 2 days ago
  }
];

export const useOrderStore = create<OrderState>()((set) => ({
      orders: mockOrders,
      emails: [],
      focusedOrderId: null,
      addOrder: async (order) => {
        set((state) => ({
          orders: [order, ...state.orders]
        }));

        if (isFirebaseActive && db) {
          try {
            await dbSet(ref(db, `orders/${order.id}`), removeUndefined(order));
            const userEmail = order.userId || order.customerDetails?.email;
            if (userEmail) {
              const key = sanitizeLocalEmail(userEmail);
              await dbSet(ref(db, `users/${key}/orders/${order.id}`), removeUndefined(order));
            }
          } catch (e: any) { 
            console.error('Add order failed on Firebase:', e);
            alert('Error syncing to database: ' + (e.message || e));
          }
        }
      },
      updateOrderStatus: async (id, status, trackingNumber) => {
        let orderToSync: Order | undefined;
        let generatedEmail: SimulatedEmail | undefined;

        set((state) => {
          const updatedOrders = state.orders.map((o) => {
            if (o.id === id) {
              const updated = { ...o, status };
              if (trackingNumber !== undefined) {
                updated.trackingNumber = trackingNumber;
              }
              return updated;
            }
            return o;
          });

          const order = updatedOrders.find(o => o.id === id);
          orderToSync = order;

          const updatedEmails = [...state.emails];

          if (order && status === 'Processing') {
            const alreadySent = state.emails.some(e => e.orderId === id && (e.type === 'payment_received' || e.type === 'order_processing_cod'));
            if (!alreadySent && order.customerDetails.email) {
              const templates = useSettingsStore.getState().emailTemplates;
              const isCOD = order.paymentMethod === 'Cash on Delivery';
              const template = isCOD ? templates?.orderProcessingCOD : templates?.paymentReceived;
              const whatsappNumber = useSettingsStore.getState().whatsappNumber;

              if (template) {
                const formattedItemsList = order.items
                  .map((item) => `- ${item.product.name} (Qty: ${item.quantity}) = LKR ${formatPriceRaw(item.product.price * item.quantity)}`)
                  .join('\n') + `\n\nSubtotal: LKR ${formatPriceRaw(order.subtotal)}\nDelivery: ${order.deliveryCharge === 0 ? 'FREE' : `LKR ${formatPriceRaw(order.deliveryCharge)}`}\nGrand Total: LKR ${formatPriceRaw(order.total)}`;

                const subject = template.subject
                  .replace(/{{orderId}}/g, order.id)
                  .replace(/{{customerName}}/g, order.customerDetails.name)
                  .replace(/{{total}}/g, formatPriceRaw(order.total))
                  .replace(/{{itemsList}}/g, 'Order Items');

                const hasItemsList = template.body.includes('{{itemsList}}');
                let bodyText = template.body
                  .replace(/{{orderId}}/g, order.id)
                  .replace(/{{customerName}}/g, order.customerDetails.name)
                  .replace(/{{total}}/g, formatPriceRaw(order.total))
                  .replace(/{{whatsappNumber}}/g, whatsappNumber)
                  .replace(/{{deliveryAddress}}/g, `${order.customerDetails.address}, ${order.customerDetails.city}`);

                if (hasItemsList) {
                  bodyText = bodyText.replace(/{{itemsList}}/g, formattedItemsList);
                }

                const plainTextBody = `${bodyText}
${!hasItemsList ? `\nOrder Details:\n${formattedItemsList}` : ''}`.trim();

                const simulatedEmail: SimulatedEmail = {
                  id: 'EML-' + Math.floor(100000 + Math.random() * 900000),
                  orderId: order.id,
                  to: order.customerDetails.email,
                  subject,
                  body: plainTextBody,
                  sentAt: new Date().toISOString(),
                  type: isCOD ? 'order_processing_cod' : 'payment_received'
                };

                generatedEmail = simulatedEmail;
                updatedEmails.push(simulatedEmail);

                // Dispatch real email in background
                sendRealEmail({
                  to_name: order.customerDetails.name,
                  to_email: order.customerDetails.email,
                  from_name: 'SensorZone.lk',
                  subject,
                  message: plainTextBody,
                  reply_to: 'sensorzonetronic@gmail.com',
                  order_id: order.id,
                  total: formatPriceRaw(order.total),
                  whatsapp_number: whatsappNumber,
                  items_list: formattedItemsList
                });
              }
            }
          }

          if (order && status === 'Dispatched') {
            const alreadySent = state.emails.some(e => e.orderId === id && e.type === 'order_dispatched');
            if (!alreadySent && order.customerDetails.email) {
              const templates = useSettingsStore.getState().emailTemplates;
              const template = templates?.orderDispatched;
              const whatsappNumber = useSettingsStore.getState().whatsappNumber;
              const trackNum = trackingNumber || order.trackingNumber || 'N/A';

              if (template) {
                const formattedItemsList = order.items
                  .map((item) => `- ${item.product.name} (Qty: ${item.quantity}) = LKR ${formatPriceRaw(item.product.price * item.quantity)}`)
                  .join('\n');

                const subject = template.subject
                  .replace(/{{orderId}}/g, order.id)
                  .replace(/{{customerName}}/g, order.customerDetails.name)
                  .replace(/{{total}}/g, formatPriceRaw(order.total))
                  .replace(/{{trackingNumber}}/g, trackNum)
                  .replace(/{{deliveryAddress}}/g, `${order.customerDetails.address}, ${order.customerDetails.city}`);

                let bodyText = template.body
                  .replace(/{{orderId}}/g, order.id)
                  .replace(/{{customerName}}/g, order.customerDetails.name)
                  .replace(/{{total}}/g, formatPriceRaw(order.total))
                  .replace(/{{trackingNumber}}/g, trackNum)
                  .replace(/{{deliveryAddress}}/g, `${order.customerDetails.address}, ${order.customerDetails.city}`)
                  .replace(/{{whatsappNumber}}/g, whatsappNumber);

                const plainTextBody = bodyText.trim();

                const simulatedEmail: SimulatedEmail = {
                  id: 'EML-' + Math.floor(100000 + Math.random() * 900000),
                  orderId: order.id,
                  to: order.customerDetails.email,
                  subject,
                  body: plainTextBody,
                  sentAt: new Date().toISOString(),
                  type: 'order_dispatched'
                };

                generatedEmail = simulatedEmail;
                updatedEmails.push(simulatedEmail);

                // Dispatch real email in background
                sendRealEmail({
                  to_name: order.customerDetails.name,
                  to_email: order.customerDetails.email,
                  from_name: 'SensorZone.lk',
                  subject,
                  message: plainTextBody,
                  reply_to: 'sensorzonetronic@gmail.com',
                  order_id: order.id,
                  total: formatPriceRaw(order.total),
                  whatsapp_number: whatsappNumber,
                  items_list: formattedItemsList
                });
              }
            }
          }

          return {
            orders: updatedOrders,
            emails: updatedEmails
          };
        });

        if (isFirebaseActive && db && orderToSync) {
          try {
            await dbSet(ref(db, `orders/${orderToSync.id}`), removeUndefined(orderToSync));
            const userEmail = orderToSync.userId || orderToSync.customerDetails?.email;
            if (userEmail) {
              const key = sanitizeLocalEmail(userEmail);
              await dbSet(ref(db, `users/${key}/orders/${orderToSync.id}`), removeUndefined(orderToSync));
            }
            if (generatedEmail) {
              await dbSet(ref(db, `emails/${generatedEmail.id}`), removeUndefined(generatedEmail));
            }
          } catch (e: any) { 
            console.error('Update order status failed on Firebase:', e);
            alert('Error syncing status to database: ' + (e.message || e));
          }
        }
      },
      updateOrder: async (id, updates) => {
        let orderToSync: Order | undefined;

        set((state) => {
          const updatedOrders = state.orders.map((o) => {
            if (o.id === id) {
              const updated = { ...o, ...updates };
              orderToSync = updated;
              return updated;
            }
            return o;
          });
          return { orders: updatedOrders };
        });

        if (isFirebaseActive && db) {
          try {
            for (const key in updates) {
              if (Object.prototype.hasOwnProperty.call(updates, key) && (updates as any)[key] !== undefined) {
                const val = (updates as any)[key];
                await dbSet(ref(db, `orders/${id}/${key}`), removeUndefined(val));
                if (orderToSync) {
                  const userEmail = orderToSync.userId || orderToSync.customerDetails?.email;
                  if (userEmail) {
                    const userKey = sanitizeLocalEmail(userEmail);
                    await dbSet(ref(db, `users/${userKey}/orders/${id}/${key}`), removeUndefined(val));
                  }
                }
              }
            }
          } catch (e: any) { 
            console.error('Update order failed on Firebase:', e);
            alert('Error updating order in database: ' + (e.message || e));
          }
        }
      },
      deleteOrder: async (id) => {
        let deletedOrder: Order | undefined;
        let emailsToDelete: SimulatedEmail[] = [];

        set((state) => {
          deletedOrder = state.orders.find((o) => o.id === id);
          emailsToDelete = state.emails.filter((e) => e.orderId === id);

          return {
            orders: state.orders.filter((o) => o.id !== id),
            emails: state.emails.filter((e) => e.orderId !== id)
          };
        });

        if (deletedOrder) {
          const productStore = useProductStore.getState();
          for (const item of deletedOrder.items) {
            // Passing negative quantity restores the stock
            const variantId = item.id.includes('-') ? item.id.split('-').pop() : undefined;
            productStore.deductProductStock(item.product.id, -item.quantity, variantId);
          }
        }

        if (isFirebaseActive && db && deletedOrder) {
          try {
            await dbSet(ref(db, `orders/${id}`), null);
            const userEmail = deletedOrder.userId || deletedOrder.customerDetails?.email;
            if (userEmail) {
              const key = sanitizeLocalEmail(userEmail);
              await dbSet(ref(db, `users/${key}/orders/${id}`), null);
            }
            for (const email of emailsToDelete) {
              await dbSet(ref(db, `emails/${email.id}`), null);
            }
          } catch (e) { console.error('Delete order failed on Firebase:', e); }
        }
      },
      addEmail: async (email) => {
        set((state) => ({
          emails: [email, ...state.emails]
        }));

        if (isFirebaseActive && db) {
          try {
            await dbSet(ref(db, `emails/${email.id}`), removeUndefined(email));
          } catch (e) { console.error('Add email failed on Firebase:', e); }
        }
      },
      clearAllOrders: async () => {
        set({ orders: [], emails: [] });

        if (isFirebaseActive && db) {
          try {
            await dbSet(ref(db, 'orders'), null);
            await dbSet(ref(db, 'emails'), null);

            // Also clear orders under each user profile path to prevent resurrection
            const usersSnap = await get(child(ref(db), 'users'));
            if (usersSnap.exists()) {
              const usersVal = usersSnap.val();
              for (const key of Object.keys(usersVal)) {
                await dbSet(ref(db, `users/${key}/orders`), null);
              }
            }
          } catch (e) { console.error('Clear all orders failed on Firebase:', e); }
        }
      }
}));
